import { Card } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { CheckCircleIcon, XCircleIcon, MinusCircleIcon } from "@heroicons/react/24/outline";
import { JSX } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import useUserStore from "@/store/shield";
import { MarginDecision, MarginDecisionEnum, MarginDecisionGroup, ParticipantOnDecisionGroup } from "@/graphql/__generated__/graphql";
import { CircleProgressIndicator } from "../../Components";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Props {
  marginDecisionGroup: MarginDecisionGroup;
  marginDecisions: MarginDecision[];
};

/**
 * @component PendingDecisionBar
 * @description Renders a status bar showing the pending margin decision status for participants.
 * It provides feedback on the progress of the decisions, displays the current or final decision,
 * and includes a hoverable indicator for individual participant statuses.
 *
 * @param {Props} props - The props for the component.
 * @param {MarginDecisionGroup} props.marginDecisionGroup - The group of margin decisions, including participants and their decisions.
 * @param {MarginDecision[]} props.marginDecisions - The array of margin decisions made by participants.
 *
 * @returns {JSX.Element} The rendered PendingDecisionBar component.
 */
const PendingDecisionBar = ({ marginDecisionGroup, marginDecisions }: Props): JSX.Element => {

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const awaitingOrganizationId = marginDecisionGroup.awaitingOrganizationId;
  const currentOrgId = useUserStore((state) => state.orgId);

  // Constants
  // Get the current decision from the group
  const currentDecision = marginDecisionGroup.currentDecision as MarginDecisionEnum;

  const currentIsReject = [MarginDecisionEnum.Reject, MarginDecisionEnum.OverruleReject].includes(currentDecision);
  const currentIsApprove = [MarginDecisionEnum.Approve, MarginDecisionEnum.OverruleApprove].includes(currentDecision);
  const finalDecisionIsApprove = currentIsApprove && awaitingOrganizationId === null;
  const finalDecisionIsReject = currentIsReject && awaitingOrganizationId === null;

  const completed = (
    finalDecisionIsApprove ||
    finalDecisionIsReject ||
    currentDecision === MarginDecisionEnum.OverruleApprove ||
    currentDecision === MarginDecisionEnum.OverruleReject
  );

  /**
   * Get the latest decision that was a request type
   *
   * This is used to determine if there is a new cycle of decisions.
   * So the indicators will be reset.
   *
   * @constant
   * @type {MarginDecision}
   * @description The latest decision that was a request type.
   */
  const latestRequestDecision = marginDecisions?.filter((md) => md.decision === MarginDecisionEnum.Request).pop();
  const latestRequestDate = latestRequestDecision ? new Date(latestRequestDecision.createdAt) : null;

  // Active Decisions in the current request cycle
  const activeDecisions = marginDecisions?.filter(md => {
    // Skip Request and Comment decisions
    if (md.decision === MarginDecisionEnum.Request || md.decision === MarginDecisionEnum.Comment) {
      return false;
    }
    // Include if no request exists or if decision is after latest request
    return !latestRequestDate || new Date(md.createdAt) > latestRequestDate;
  });

  // Total Decisions in the current request cycle
  const totalDecisions = new Set(activeDecisions?.map(md => md?.userOnDecision.id)).size;

  // Build a map of each organization's latest decision
  const organizationDecisionsMap = activeDecisions?.reduce((map, decision) => {
    const orgId = decision?.userOnDecision.organization.id;
    if (orgId) {
      map[orgId] = decision;
    }
    return map;
  }, {} as { [organizationId: string]: MarginDecision });

  /**
   * @constant
   * @type {string}
   * @description Message indicating the current status of the margin decision process based on awaiting participant.
   */
  let statusMessage: string = "";
  if (awaitingOrganizationId === null) {
    statusMessage = t("pendingdecisionbar.decisionreached");
  } else if (currentIsReject) {
    statusMessage = t("pendingdecisionbar.rerequestapproval");
  } else if (awaitingOrganizationId === currentOrgId) {
    statusMessage = t("pendingdecisionbar.awaitingdecision");
  } else {
    // Find the awaiting organization name
    const awaitingOrganizationName =  marginDecisionGroup.participantsOnDecisionGroup.find((participant: ParticipantOnDecisionGroup) => participant.id === awaitingOrganizationId)?.organizationName || t("chatbubble.unknownorganization");
    statusMessage = t("pendingdecisionbar.awaitingorganizatioon", { organizationName: awaitingOrganizationName });
  }

  // Determine the decision title based on 'awaitingOrganizationId'
  const decisionTitle = awaitingOrganizationId === null ? t("pendingdecisionbar.finaldecision") : t("pendingdecisionbar.currentdecision");

  // Get margin decisions and participants
  const participants = marginDecisionGroup.participantsOnDecisionGroup || [];

  /**
   * @constant
   * @type {number}
   * @description The percentage of participants who have made a decision.
   */
  const totalParticipants: number = participants.length;

  const progress = totalParticipants > 0 ? (totalDecisions / totalParticipants) * 100 : 0;

  const marginDecisionColourMapping: Record<string, string> = {
    [MarginDecisionEnum.Approve]: "#84CC16", // Green for approve
    [MarginDecisionEnum.OverruleApprove]: "#84CC16", // Green for overrule approve
    [MarginDecisionEnum.Reject]: "#EF4444", // Red for reject
    [MarginDecisionEnum.OverruleReject]: "#EF4444", // Red for overrule reject
  };

  /**
   * @constant
   * @type {Array}
   * @description Array of participant statuses, each with an organization name and status icon.
   */
  const participantStatuses: { organizationName: string; statusIcon: JSX.Element; }[] = participants.map((participant) => {
    const organizationId = participant?.id;
    const organizationName = participant?.organizationName;

    // Get the latest decision for this organization
    const latestMarginDecision = organizationDecisionsMap[organizationId];

    // Determine the status icon based on the latest decision
    let statusIcon: JSX.Element;
    if (latestMarginDecision) {
      if ([MarginDecisionEnum.Approve, MarginDecisionEnum.OverruleApprove].includes(latestMarginDecision.decision)) {
        statusIcon = <CheckCircleIcon className="icon-md text-green-500" />;
      } else if ([MarginDecisionEnum.Reject, MarginDecisionEnum.OverruleReject].includes(latestMarginDecision.decision)) {
        statusIcon = <XCircleIcon className="icon-md text-red-500" />;
      } else {
        statusIcon = <MinusCircleIcon className="icon-md text-orange-500" />;
      }
    } else {
      // No decision made yet
      statusIcon = <MinusCircleIcon className="icon-md text-gray-500" />;
    }

    return {
      organizationName,
      statusIcon,
    };
  });

  return (
    <Card className="py-4 px-4 flex flex-row items-center justify-between text-sm">
      <div className="max-w-[70%]">
        {/* Display the appropriate status message */}
        <p
          className={cn(
            currentIsReject ? "text-red-500" :
              awaitingOrganizationId === currentOrgId
                ? "text-yellow-500"
                : "text-gray-400"
          )}
        >
          {statusMessage}
        </p>
      </div>
      <div className="flex items-center gap-2 font-semibold">
        {/* Display 'Final Decision' or 'Current Decision' based on the state */}
        <p className="whitespace-nowrap">{decisionTitle}</p>
        <HoverCard openDelay={0}>
          <HoverCardTrigger className="flex items-center justify-center cursor-help">
            {/* Progress Indicator with calculated progress */}
            <Button
              variant="ghost"
              size="icon"
              className="w-[22px] h-fit p-0 hover:cursor-help"
              type="button"
            >
              <CircleProgressIndicator
                progress={completed || currentDecision === MarginDecisionEnum.Reject ? 100 : progress}
                tenderDecision={currentDecision}
                colourMapping={marginDecisionColourMapping}
              />
            </Button>
          </HoverCardTrigger>
          <HoverCardContent className="min-w-[250px] w-auto max-w-[400px]" align="end" side="top">
            <div className="flex-row space-y-2">
              {/* Dynamically render participant statuses */}
              {participantStatuses.map((participantStatus, index) => (
                <div key={index} className="flex items-center gap-2">
                  {participantStatus.statusIcon}
                  <p>{participantStatus.organizationName}</p>
                </div>
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
        {/* Display the current tender decision */}
        <p className="whitespace-nowrap">
          {currentIsApprove
            ? t("components:pendingdecisionbar.margindecisionstatus.approve") :
            currentIsReject
              ? t("components:pendingdecisionbar.margindecisionstatus.reject") :
              currentDecision === MarginDecisionEnum.Request ?
                t("components:pendingdecisionbar.margindecisionstatus.request") : ""
          }
        </p>
      </div>
    </Card>
  );
};

export default PendingDecisionBar;
